// src/pages/WordBuildingGamePage.tsx
// ENHANCED VERSION: Context-rich learning adventure for ND learners

import React, { useState, useEffect, useRef, useMemo } from 'react'
import { useLocation } from 'wouter'
import { Volume2, Heart } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { audio, storage } from '@/lib/utils'
import { AdaptiveWordBank } from '@/data/wordBank'
import type { TtsAccent } from '@/types'
import { useAnalytics } from '../hooks/useAnalytics'
import { useSessionStore } from '../stores/sessionStore'
import { useCurrentUserId } from '@/lib/auth-utils'
import { useChildAuth } from '@/contexts/ChildAuthContext'
import { PhonicsChunker } from '../utils/PhonicsChunker';
import { PatternSelector } from '../utils/PatternSelector';
import { capacityMonitor } from '../services/capacityMonitor'
import type { CapacityAlert } from '../services/capacityMonitor'
//import { CapacityAlertModal } from '@/components/CapacityAlertModal'

interface WordBuildingGamePageProps {
  theme: string
}

// Enhanced word entry interface
interface EnhancedWordEntry {
  id: string
  word: string
  complexity: string
  chunks: string[]
  alternative_chunks: string[]
  phonics_focus: string
  themes: string[]
  high_frequency: boolean
  meaning_support: string
  difficulty_level: number
  context_introduction?: string
  completion_sentence?: string
  visual_context?: string
  personal_connection_question?: string
  story_connection?: string
  pattern_family?: string
  celebration_message?: string
}

// Pattern discovery tracking
interface PatternProgress {
  family: string
  wordsCompleted: string[]
  totalEncountered: number
  lastCelebrated: number
}

// Theme choice interface
interface ThemeChoice {
  name: string
  display: string
  emoji: string
  description: string
}

// Mood-specific UI configurations
const getMoodStyles = (mood: string) => {
  switch(mood) {
    case 'overwhelmed':
    case 'tired':
      return {
        containerBg: 'bg-[#FAF9F6]', // Flat cream - zero visual noise
        cardBg: 'bg-white/60',
        spacing: 'space-y-12', // Extra spacing between elements
        fontSize: 'text-3xl', // Larger text
        buttonSize: 'px-8 py-6 text-xl', // Bigger buttons
        chunkSpacing: 'gap-6', // More space between chunks
        hideProgress: true, // Don't show word count
        animationSpeed: 'duration-700', // Slower animations
        celebrationVolume: 0.3, // Quieter sounds
        shadow: '', // NO shadow - reduces visual depth
        buttonShadow: '',
        buttonHoverShadow: '',
        buttonActiveShadow: '',
        buttonCorners: 'rounded-3xl', // 24px extra soft
        buttonTransform: '', // NO hover movement
      }
    case 'energetic':
    case 'excited':
      return {
        containerBg: 'bg-gradient-to-b from-amber-100 to-sky-100', // Simpler, vertical only
        cardBg: 'bg-white',
        spacing: 'space-y-4', // Tight spacing
        fontSize: 'text-xl', // Smaller text, more fits on screen
        buttonSize: 'px-4 py-3 text-base',
        chunkSpacing: 'gap-3',
        hideProgress: false,
        animationSpeed: 'duration-200', // Fast animations
        celebrationVolume: 1.0, // Full volume
        shadow: 'shadow-md', // More pronounced shadow
        buttonShadow: 'shadow-lg',
        buttonHoverShadow: 'hover:shadow-xl',
        buttonActiveShadow: 'active:shadow-md',
        buttonCorners: 'rounded-2xl',
        buttonTransform: 'hover:-translate-y-1', // Bigger lift
      }
    default: // focused, curious, steady
      return {
        containerBg: 'bg-gradient-to-b from-indigo-50 via-white to-green-50',
        cardBg: 'bg-white',
        spacing: 'space-y-6',
        fontSize: 'text-2xl',
        buttonSize: 'px-6 py-4 text-lg',
        chunkSpacing: 'gap-4',
        hideProgress: false,
        animationSpeed: 'duration-300',
        celebrationVolume: 0.7,
        shadow: 'shadow-sm', // Subtle shadow
        buttonShadow: 'shadow-md',
        buttonHoverShadow: 'hover:shadow-lg',
        buttonActiveShadow: 'active:shadow-sm',
        buttonCorners: 'rounded-2xl',
        buttonTransform: 'hover:-translate-y-0.5', // Subtle lift
      }
  }
}

const WordBuildingGamePage: React.FC<WordBuildingGamePageProps> = ({ theme }) => {
  // Core game state
  const [currentWordIndex, setCurrentWordIndex] = useState(0)
  const [arrangedChunks, setArrangedChunks] = useState<string[]>([])
  const [availableChunks, setAvailableChunks] = useState<string[]>([])
  const [isWordComplete, setIsWordComplete] = useState(false)
  const [showCelebration, setShowCelebration] = useState(false)
  const [showLockIn, setShowLockIn] = useState(false)
  const [showWrongOrderMessage, setShowWrongOrderMessage] = useState(false)
  const [toastMessage, setToastMessage] = useState<string>('')
  const [showToast, setShowToast] = useState(false)
  const [wordsCompleted, setWordsCompleted] = useState<string[]>([])
  const [isReading, setIsReading] = useState(false)
  const [autoReadEnabled, setAutoReadEnabled] = useState(() => 
    storage.get('auto-read-success', true)
  )
  const [, setLocation] = useLocation()

  // Enhanced engagement state
  const [currentWordData, setCurrentWordData] = useState<EnhancedWordEntry | null>(null)
  const [showContextIntro, setShowContextIntro] = useState(true)
  const [showPatternIntro, setShowPatternIntro] = useState(false)
  const [currentPatternInfo, setCurrentPatternInfo] = useState<any>(null)
  const [wordsInCurrentPattern, setWordsInCurrentPattern] = useState(0)
  const [showPatternReflection, setShowPatternReflection] = useState(false)
  const [patternProgress, setPatternProgress] = useState<Record<string, PatternProgress>>({})
  const [sessionPatterns, setSessionPatterns] = useState<Set<string>>(new Set())
  const [lastPatternCelebration, setLastPatternCelebration] = useState<string>('')
  
  // ADD ANALYTICS HOOKS
  const userId = useCurrentUserId()
  const { childSession } = useChildAuth()
  const childId = childSession?.childId
  const { trackAnyActivity } = useAnalytics(childId || userId || undefined)
  const { currentBrainState } = useSessionStore()

  // Performance tracking (invisible to user)
  const [wordStartTime, setWordStartTime] = useState<number>(Date.now())
  const [hintsUsed, setHintsUsed] = useState<number>(0)
  const [resetsUsed, setResetsUsed] = useState<number>(0)
  
  // Adaptive system
  const adaptiveWordBank = useRef<AdaptiveWordBank | null>(null)
  const hasPlayedSuccessTTS = useRef<boolean>(false)
  const hasShownPatternIntro = useRef<boolean>(false)
  const [currentWords, setCurrentWords] = useState<string[]>([])
  const [isInitialized, setIsInitialized] = useState(false)  
  const [currentPattern, setCurrentPattern] = useState<string>('cvc')

  const [capacityAlert, setCapacityAlert] = useState<CapacityAlert | null>(null)
  const [showCapacityModal, setShowCapacityModal] = useState(false)
  const [wordDifficulty, setWordDifficulty] = useState<'easy' | 'regular' | 'challenge'>('regular')
  const toggleCalmCorner = useSessionStore((state) => state.toggleCalmCorner)
  const [showBreathingBreak, setShowBreathingBreak] = useState(false)

  // Mood-based adaptive settings
  const adaptiveSettings = useMemo(() => {
    const moodMap = {
      overwhelmed: { wordCount: 4, fontSize: 'text-2xl', hintsEnabled: true },
      tired: { wordCount: 4, fontSize: 'text-2xl', hintsEnabled: true },
      energetic: { wordCount: 8, fontSize: 'text-xl', hintsEnabled: false },
      excited: { wordCount: 8, fontSize: 'text-xl', hintsEnabled: false },
      focused: { wordCount: 6, fontSize: 'text-xl', hintsEnabled: false },
      curious: { wordCount: 6, fontSize: 'text-xl', hintsEnabled: false }
    }
    const moodId = currentBrainState?.id || 'focused'
    return moodMap[moodId] || { wordCount: 6, fontSize: 'text-xl', hintsEnabled: false }
  }, [currentBrainState])

  // Initialize adaptive system with pattern selection
  useEffect(() => {
    const initializeAdaptiveSystem = async () => {
      const userAge = storage.get('user-age', 10)
      adaptiveWordBank.current = new AdaptiveWordBank(userAge)
      
      await new Promise(resolve => setTimeout(resolve, 500))
      
      // Get next pattern from PatternSelector
      const childId = userId || 'guest';
      const pattern = await PatternSelector.getNextPattern(childId);
      console.log(`🎯 Selected pattern from PatternSelector: ${pattern}`);
      setCurrentPattern(pattern);

      // Pattern comes directly from PatternSelector with correct IDs
      console.log(`🎯 Using pattern from PatternSelector: ${pattern}`);

      // Get mood-based word count
      const moodWordCount = adaptiveSettings.wordCount;

      // Get words using FULL pattern ID
      const words = adaptiveWordBank.current.getWordsForPattern(pattern, theme);
      const patternInfo = adaptiveWordBank.current.getPatternInfo(pattern);

      if (patternInfo) {
        setCurrentPatternInfo(patternInfo);
        console.log(`✅ Pattern info loaded: ${patternInfo.display_name}`);
      } else {
        console.warn(`⚠️ Pattern info NOT found for: ${pattern}`);
      }

      // Limit by mood
      const selectedWords = words.slice(0, moodWordCount);

      if (selectedWords.length === 0) {
        console.warn(`⚠️ No words for pattern ${pattern} + theme ${theme}, using fallback`);
        setCurrentWords(['HACK', 'CRASH', 'DASH', 'BLAST']); // Emergency fallback
      } else {
        console.log(`✅ Loaded ${selectedWords.length} words for ${pattern} + ${theme}`);
        setCurrentWords(selectedWords);
      }

      //setShowPatternIntro(true); // Show pattern intro first
      
      setIsInitialized(true);
      const savedPatterns = storage.get('pattern-progress', {})
      setPatternProgress(savedPatterns)

      // Initialize capacity monitor with historical baseline
      try {
        const moodId = currentBrainState?.id || 'focused'
        const mood = moodId === 'overwhelmed' || moodId === 'tired' ? 'overwhelmed' 
                  : moodId === 'energetic' || moodId === 'excited' ? 'high-energy' 
                  : 'steady'
        console.log('🧪 Initializing capacity monitor:', { userId: userId || 'guest', mood })
        await capacityMonitor.initialize(userId || 'guest', mood as 'overwhelmed' | 'steady' | 'high-energy')
        console.log('✅ Capacity monitor initialized successfully')
      } catch (error) {
        console.error('❌ Capacity monitor initialization failed:', error)
      }
    }

    initializeAdaptiveSystem()
  }, [theme, userId, currentBrainState])

  // Get current word
  const currentWord = currentWords[currentWordIndex] || ''
  
  // Load enhanced word data from JSON
  useEffect(() => {
    const loadWordData = async () => {
      if (!currentWord) return
      
      try {
        // In real implementation, this would fetch from your words.json
        // For now, creating enhanced data structure
        const enhancedData: EnhancedWordEntry = {
          id: `w${currentWordIndex + 1}`,
          word: currentWord,
          complexity: 'regular',
          chunks: PhonicsChunker.chunk(currentWord.toLowerCase(), currentPattern),
          alternative_chunks: [],
          phonics_focus: 'pattern_focus',
          themes: [theme],
          high_frequency: true,
          meaning_support: `A ${currentWord} is an important word to learn!`,
          difficulty_level: 2,
          context_introduction: generateContextIntro(currentWord, theme),
          completion_sentence: generateCompletionSentence(currentWord, theme),
          visual_context: generateVisualContext(currentWord),
          personal_connection_question: generatePersonalQuestion(currentWord),
          story_connection: generateStoryConnection(currentWord, theme),
          pattern_family: detectPatternFamily(currentWord),
          celebration_message: generateCelebrationMessage(currentWord)
        }
        
        setCurrentWordData(enhancedData)
        setShowContextIntro(true)
        //setShowMeaningIntegration(false)
        //setShowPatternCelebration(false)
        
      } catch (error) {
        console.error('Error loading word data:', error)
      }
    }
    
    loadWordData()
  }, [currentWord, theme, currentWordIndex])
  
  // Initialize word chunks when word changes
  useEffect(() => {
    if (currentWord && currentPattern) {
      // Always use PhonicsChunker - no JSON chunks anymore
      let chunks = PhonicsChunker.chunk(currentWord.toLowerCase(), currentPattern).map(c => c.toUpperCase())

      console.log(`🔍 Result: word="${currentWord}", pattern="${currentPattern}", chunks=`, chunks)
      
      const shuffledChunks = [...chunks].sort(() => Math.random() - 0.5)
      setAvailableChunks(shuffledChunks)
      setArrangedChunks([])
      setIsWordComplete(false)
      setShowCelebration(false)
      setShowWrongOrderMessage(false)
      setWordStartTime(Date.now())
      setHintsUsed(0)
      setResetsUsed(0)
    }
  }, [currentWord, currentWordIndex, currentPattern])

  // Track when user starts new word (existing useEffect likely handles this)
  useEffect(() => {
    if (currentWord && currentWords.length > 0) {
      setWordStartTime(Date.now())
      setHintsUsed(0)
      setResetsUsed(0)
    }
  }, [currentWordIndex, currentWord])

  // Track Calm Corner effectiveness
  useEffect(() => {
    const isInCalmCorner = useSessionStore.getState().isInCalmCorner
    
    if (!isInCalmCorner && capacityAlert?.level === 'urgent') {
      // They returned from Calm Corner - reset alert and log success
      console.log('✅ Returned from Calm Corner intervention')
      setCapacityAlert(null)
    }
  }, [useSessionStore.getState().isInCalmCorner])

  // Context generation helpers
  const generateContextIntro = (word: string, theme: string): string => {
    const contexts: Record<string, string[]> = {
      tech: [
        `The system needs this code! Build: ${word.toUpperCase()}`,
        `Security detected this pattern! Build: ${word.toUpperCase()}`,
        `The hacker found the key! Build: ${word.toUpperCase()}`
      ],
      survival: [
        `The explorer spotted this! Build: ${word.toUpperCase()}`,
        `You need this to survive! Build: ${word.toUpperCase()}`,
        `Found in the wilderness! Build: ${word.toUpperCase()}`
      ],
      mystery: [
        `Detective Sam found a clue! Build this mystery word: ${word.toUpperCase()}`,
        `The evidence points to: ${word.toUpperCase()}`,
        `Case file shows: ${word.toUpperCase()}`
      ],
      action: [
        `The fighter needs: ${word.toUpperCase()}`,
        `Combat ready! Build: ${word.toUpperCase()}`,
        `Mission critical word: ${word.toUpperCase()}`
      ],
      'sci-fi': [
        `Space station detected: ${word.toUpperCase()}`,
        `Alien signal decoded: ${word.toUpperCase()}`,
        `Galactic command: Build ${word.toUpperCase()}`
      ]
    }
    
    const themeContexts = contexts[theme] || contexts.tech
    return themeContexts[Math.floor(Math.random() * themeContexts.length)]
  }

  const generateCompletionSentence = (word: string, theme: string): string => {
    const sentences: Record<string, string[]> = {
      tech: [
        `The ${word.toUpperCase()} code executed perfectly!`,
        `System upgraded with ${word.toUpperCase()}!`,
        `${word.toUpperCase()} unlocked the firewall!`
      ],
      survival: [
        `You found ${word.toUpperCase()} in the wild!`,
        `${word.toUpperCase()} saved the day!`,
        `Survival skill unlocked: ${word.toUpperCase()}!`
      ],
      mystery: [
        `The ${word.toUpperCase()} solved the case!`,
        `Evidence confirms: ${word.toUpperCase()}!`,
        `Mystery revealed: ${word.toUpperCase()}!`
      ],
      action: [
        `Fighter mastered ${word.toUpperCase()}!`,
        `Combat move: ${word.toUpperCase()} complete!`,
        `Victory achieved with ${word.toUpperCase()}!`
      ],
      'sci-fi': [
        `${word.toUpperCase()} powered the starship!`,
        `Alien tech decoded: ${word.toUpperCase()}!`,
        `Warp speed with ${word.toUpperCase()}!`
      ]
    }
    
    const themeSentences = sentences[theme] || sentences.tech
    return themeSentences[Math.floor(Math.random() * themeSentences.length)]
  }

  const generateVisualContext = (word: string): string => {
    const visuals: Record<string, string> = {
      ocean: '🌊 Deep blue water with gentle waves',
      whale: '🐋 Gentle giant swimming peacefully',
      fish: '🐠 Colorful swimmer darting through coral',
      ship: '⛵ Brave vessel sailing the seas',
      space: '🌌 Vast cosmic expanse full of wonder',
      rocket: '🚀 Powerful explorer reaching for stars',
      star: '⭐ Twinkling light in the darkness',
      moon: '🌙 Glowing companion in the night',
      cat: '🐱 Friendly feline companion',
      dog: '🐶 Loyal four-legged friend'
    }
    
    return visuals[word.toLowerCase()] || `✨ Something special: ${word}`
  }

  const generatePersonalQuestion = (word: string): string => {
    const questions: Record<string, string> = {
      ocean: "Have you ever seen the ocean or dreamed about underwater adventures?",
      star: "Do you like to look at stars in the night sky?",
      friend: "What makes someone a really good friend?",
      happy: "What makes you feel really happy?",
      home: "What's your favorite thing about your home?"
    }
    
    return questions[word.toLowerCase()] || `When have you seen or thought about ${word}?`
  }

  const generateStoryConnection = (word: string, theme: string): string => {
    return `Great job! ${word.toUpperCase()} will appear in your next ${theme} adventure story!`
  }

  const detectPatternFamily = (word: string): string => {
    const patterns: Record<string, RegExp> = {
      'vowel_team_family': /[aeiou]{2}/i,
      'silent_e_family': /[aeiou][bcdfghjklmnpqrstvwxyz]e$/i,
      'consonant_blend_family': /^[bcdfghjklmnpqrstvwxyz]{2}/i,
      'ending_le_family': /le$/i,
      'double_consonant_family': /([bcdfghjklmnpqrstvwxyz])\1/i
    }
    
    for (const [family, pattern] of Object.entries(patterns)) {
      if (pattern.test(word)) return family
    }
    
    return 'basic_pattern_family'
  }

  const generateCelebrationMessage = (word: string): string => {
    const family = detectPatternFamily(word)
    
    // Use pattern info if available, otherwise fall back to family detection
    if (currentPatternInfo?.celebration_message) {
      return currentPatternInfo.celebration_message.replace('{word}', word.toUpperCase())
    }
    
    const messages: Record<string, string> = {
      'vowel_team_family': `Excellent! ${word.toUpperCase()} has a vowel team that works together!`,
      'silent_e_family': `Amazing! ${word.toUpperCase()} uses the magic silent E pattern!`,
      'consonant_blend_family': `Fantastic! ${word.toUpperCase()} starts with blended consonants!`,
      'ending_le_family': `Wonderful! ${word.toUpperCase()} ends with the -LE pattern!`,
      'double_consonant_family': `Great work! ${word.toUpperCase()} has double consonants!`,
      'basic_pattern_family': `Perfect! You built ${word.toUpperCase()} beautifully!`
    }
    
    return messages[family] || `Outstanding work on ${word.toUpperCase()}!`
  }

  // Pattern discovery system - FIXED to avoid duplicates
  const checkPatternDiscovery = (word: string) => {
    if (!currentWordData) return
    
    const family = currentWordData.pattern_family || 'basic_pattern_family'
    
    // Update pattern progress
    setPatternProgress(prev => {
      const current = prev[family] || { family, wordsCompleted: [], totalEncountered: 0, lastCelebrated: 0 }
      
      // Check if word is already in the completed list to avoid duplicates
      if (current.wordsCompleted.includes(word.toUpperCase())) {
        return prev // Don't add duplicate
      }
      
      const updated = {
        ...current,
        wordsCompleted: [...current.wordsCompleted, word.toUpperCase()],
        totalEncountered: current.totalEncountered + 1
      }
      
      // Celebrate pattern mastery at milestones (3, 5, 7+ words)
      const milestones = [3, 5, 7]
      if (milestones.includes(updated.wordsCompleted.length) && updated.lastCelebrated < updated.wordsCompleted.length) {
        //setLastPatternCelebration(createPatternCelebration(family, updated.wordsCompleted))
        //setShowPatternCelebration(true)
        updated.lastCelebrated = updated.wordsCompleted.length
      }
      
      // Save to storage
      const newProgress = { ...prev, [family]: updated }
      storage.set('pattern-progress', newProgress)
      
      return newProgress
    })
  }

  const createPatternCelebration = (family: string, words: string[]): string => {
    const familyNames: Record<string, string> = {
      'vowel_team_family': 'vowel team',
      'silent_e_family': 'silent E',
      'consonant_blend_family': 'consonant blend',
      'ending_le_family': '-LE ending',
      'double_consonant_family': 'double consonant',
      'basic_pattern_family': 'word building'
    }
    
    const name = familyNames[family] || 'pattern'
    const count = words.length
    const recentWords = words.slice(-3).join(', ')
    
    if (count === 3) {
      return `🎉 Amazing! You've mastered 3 ${name} words: ${recentWords}! You're becoming a ${name} expert!`
    } else if (count === 5) {
      return `🌟 Incredible! You've conquered 5 ${name} words! You're a ${name} champion!`
    } else {
      return `🏆 Outstanding! You've mastered ${count} ${name} words! You're a true ${name} master!`
    }
  }

  // Word completion with enhanced celebrations - FIXED wrong order logic
  useEffect(() => {
    if (arrangedChunks.length === 0) {
      setShowWrongOrderMessage(false)  // Clear any lingering message
      return
    }
    const checkWordCompletion = () => {
      if (arrangedChunks.length === 0) return
      
      const arrangedWord = arrangedChunks.join('').toUpperCase()
      const targetWord = currentWord.toUpperCase()

      console.log('🔍 Checking completion:', { 
        arrangedWord, 
        targetWord, 
        match: arrangedWord === targetWord,
        arrangedChunks 
      })
      
      if (arrangedWord === targetWord) {
        console.log(`✅ Word completed: ${currentWord}`)
        
        // IMMEDIATE FEEDBACK: Lock-in animation + sound
        setShowLockIn(true)
        
        // Play soft click sound using Web Audio API
        try {
          const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)()
          const oscillator = audioContext.createOscillator()
          const gainNode = audioContext.createGain()
          
          oscillator.connect(gainNode)
          gainNode.connect(audioContext.destination)
          
          oscillator.frequency.value = 800 // Higher pitch = softer click
          oscillator.type = 'sine'
          
          gainNode.gain.setValueAtTime(moodStyles.celebrationVolume * 0.3, audioContext.currentTime)
          gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.1)
          
          oscillator.start(audioContext.currentTime)
          oscillator.stop(audioContext.currentTime + 0.1)
        } catch (error) {
          console.log('Audio feedback unavailable:', error)
        }
        
        setTimeout(() => setShowLockIn(false), 400) // 400ms lock-in pulse
        
        setIsWordComplete(true)
        setShowContextIntro(false)
        setShowWrongOrderMessage(false)
        setWordsCompleted(prev => [...prev, currentWord])

        // 🔔 Trigger pattern insight ONCE after first successful word
        if (!hasShownPatternIntro.current && currentPatternInfo) {
          hasShownPatternIntro.current = true
          setShowPatternIntro(true)
        }
        
        // Show toast instead of modal
        setToastMessage(`${currentWord} ✓`)
        setShowToast(true)
        setTimeout(() => setShowToast(false), 2000)
        
        // Track pattern discovery
        checkPatternDiscovery(currentWord)

        // Track completed word
        const wordMinutes = Math.max(1, Math.round((Date.now() - wordStartTime) / 60000))
        const wordAccuracy = Math.round(((currentWord.length - hintsUsed) / currentWord.length) * 100)
        trackAnyActivity(`word_building`, `${theme}: ${currentWord}`, wordMinutes, {
          accuracy: wordAccuracy,
          skills: ['phonics', 'word_building'],
          completed: true
        }).catch(err => console.log('Word tracking failed (non-critical):', err))
        
        // Performance tracking
        const completionTime = Date.now() - wordStartTime
        console.log(`📊 Word "${currentWord}" completed in ${completionTime}ms with ${hintsUsed} hints and ${resetsUsed} resets`)

        console.log('🧪 Recording interaction:', { completionTime, hintsUsed, resetsUsed, wordDifficulty })
        // Record interaction for capacity monitoring
        const alert = capacityMonitor.recordInteraction({
          correct: true,
          responseTime: completionTime,
          taskType: 'word-building',
          difficulty: wordDifficulty === 'easy' ? 1 : wordDifficulty === 'regular' ? 2 : 3,
          toolsUsed: [
            ...(hintsUsed > 0 ? ['hints'] : []),
            ...(resetsUsed > 0 ? ['reset'] : [])
          ]
        })

        if (alert) {
          console.log('⚠️ Capacity alert:', alert)
          setCapacityAlert(alert)
          
          // Tier 1: Silent adjustment
          if (alert.level === 'subtle') {
            console.log('🔇 Tier 1: Silent difficulty adjustment')
            setWordDifficulty(prev => 
              prev === 'challenge' ? 'regular' : prev === 'regular' ? 'easy' : 'easy'
            )
          }
          
          // Tier 2: Auto-adjust + mandatory 10-second breathing break
          if (alert.level === 'moderate') {
            console.log('🌬️ Tier 2: Auto-adjusting difficulty + inserting breathing break')
            setWordDifficulty('easy') // Force to easy immediately
            
            // Insert mandatory breathing break
            setShowBreathingBreak(true)
            setTimeout(() => {
              setShowBreathingBreak(false)
            }, 10000) // 10 seconds
          }
          
          // Tier 3: Force calm corner (no choice)
          if (alert.level === 'urgent') {
            console.log('🚨 Tier 3: Critical intervention - forcing calm corner')
            toggleCalmCorner()
            setCapacityAlert(null)
          }
        }
        
        // ADD ANALYTICS TRACKING FOR WORD SUCCESS
        if (userId) {
          const timeInMinutes = Math.max(0.1, Math.round(completionTime / 60000))
          
          // Track breakthrough for challenging words
          if (currentWord.length > 6 && hintsUsed <= 1) {
            trackAnyActivity(
              'word_building',
              `Breakthrough: ${currentWord}`,
              0.1,
              {
                skills: ['challenging_words'],
                completed: true
              }
            ).catch(error =>
              console.log('Breakthrough analytics failed:', error)
            )
          }
        }
        
        // Auto-play success TTS message (if enabled) - ONLY ONCE
        if (autoReadEnabled && !showCelebration && !hasPlayedSuccessTTS.current) {
          hasPlayedSuccessTTS.current = true
          setTimeout(async () => {
            if (!isReading) {
              const successMessage = `You have built ${currentWord.toUpperCase()}!`
              await speakText(successMessage)
            }
          }, 800)
        }
        
        // Update adaptive system with your existing recordWordPerformance method
        if (adaptiveWordBank.current) {
          const completionTime = Date.now() - wordStartTime
          adaptiveWordBank.current.recordWordPerformance({
            timePerWord: completionTime,
            hintsUsed,
            resets: resetsUsed,
            completed: true,
            difficulty: 'regular' as 'easy' | 'regular' | 'challenge',
            timestamp: Date.now()
          })
        }
        return
      } else if (arrangedChunks.length >= PhonicsChunker.chunk(currentWord.toLowerCase(), currentPattern).length && !isWordComplete && !showCelebration ) {
        // Only show wrong order message if word is NOT complete, NOT celebrating, and NOT in success state
        setShowWrongOrderMessage(true)
        setTimeout(() => setShowWrongOrderMessage(false), 4000)
      }
    }
    
    checkWordCompletion()
    // Auto-dismiss pattern insight after 5 seconds (after it actually appears)
    if (showPatternIntro) {
      const timer = setTimeout(() => setShowPatternIntro(false), 5000)
      return () => clearTimeout(timer)
    }
  }, [arrangedChunks, currentWord, wordStartTime, hintsUsed, resetsUsed, isWordComplete, showCelebration, userId])

  // FIXED: Back to your original working TTS implementation
  const speakText = async (text: string, isChunk = false): Promise<void> => {
    return new Promise(async (resolve) => {
      if (!audio.isSpeechSynthesisSupported() || !('speechSynthesis' in window)) {
        console.warn('Speech synthesis not supported')
        resolve()
        return
      }
  
      try {
        setIsReading(true)
        console.log(`🔊 Speaking: "${text}"`)
        
        // Stop any current speech
        speechSynthesis.cancel()
        await new Promise(r => setTimeout(r, 100))
  
        // Get current accent - FORCE US/UK for phonics (Indian TTS quality not good enough)
        const savedAccent = storage.get('tts-accent', 'GB') as TtsAccent
        const phonicsAccent = savedAccent === 'IN' ? 'GB' : savedAccent
        console.log(`🎤 Using accent: ${phonicsAccent} (saved: ${savedAccent})`)

        // FIXED: Use the new async voice selection
        await audio.speak(text, {
          accent: phonicsAccent,
          rate: isChunk ? 0.6 : 0.7,
          pitch: 0.9,
          volume: 0.8
        })
        
      } catch (error) {
        console.error('TTS error:', error)
      } finally {
        setIsReading(false)
        resolve()
      }
    })
  }

  // Phonetic chunking fallback
  const getPhoneticChunks = (word: string): string[] => {
    const upperWord = word.toUpperCase()
    
    const patterns = [
      { pattern: /^(QU)(.+)$/i, chunks: (match: string[]) => [match[1], match[2]] },
      { pattern: /^(.+)(ING|TION|SION)$/i, chunks: (match: string[]) => [match[1], match[2]] },
      { pattern: /^(.+)(ED|ER|EST|LY)$/i, chunks: (match: string[]) => [match[1], match[2]] },
      { pattern: /^(.+)([BCDFGHJKLMNPQRSTVWXYZ])LE$/i, chunks: (match: string[]) => [match[1], match[2] + match[3]] },
      { pattern: /^(.+)([BCDFGHJKLMNPQRSTVWXYZ])\2(.+)$/i, chunks: (match: string[]) => [match[1] + match[2], match[2] + match[3]] },
      { pattern: /^(BL|BR|CL|CR|DR|FL|FR|GL|GR|PL|PR|SC|SK|SL|SM|SN|SP|ST|SW|TR|TW|TH|SH|CH|WH)(.+)$/i, chunks: (match: string[]) => [match[1], match[2]] }
    ]
    
    for (const { pattern, chunks } of patterns) {
      const match = upperWord.match(pattern)
      if (match) {
        const result = chunks(match)
        if (result.every(chunk => chunk.length > 0)) {
          return result
        }
      }
    }
    
    if (upperWord.length >= 4) {
      const vowels = 'AEIOU'
      
      if (upperWord.endsWith('LE') && upperWord.length > 3) {
        const beforeLE = upperWord.slice(0, -2)
        const lastConsonant = upperWord.slice(-3, -2)
        if (!vowels.includes(lastConsonant)) {
          return [beforeLE.slice(0, -1), lastConsonant + 'LE']
        }
      }
      
      for (let i = 1; i < upperWord.length - 1; i++) {
        const prev = upperWord[i - 1]
        const curr = upperWord[i]
        const next = upperWord[i + 1]
        
        if (vowels.includes(prev) && !vowels.includes(curr) && vowels.includes(next)) {
          return [upperWord.slice(0, i + 1), upperWord.slice(i + 1)]
        }
      }
      
      const mid = Math.ceil(upperWord.length / 2)
      return [upperWord.slice(0, mid), upperWord.slice(mid)]
    }
    
    return [upperWord]
  }

  // Interaction handlers - Fixed TTS for chunks and hear word
  const handleChunkClick = async (chunk: string, index: number, source: 'available' | 'arranged') => {
    if (isReading || isWordComplete) return
    
    console.log(`🔊 Chunk clicked: ${chunk} from ${source}`)
    
    // Convert multi-letter chunks to letter names with spaces
    const ttsToSpeak = chunk.length > 1 
    ? chunk.toUpperCase().split('').join(' ')  // "SH" becomes "S H", "TR" becomes "T R"
    : chunk.toLowerCase()  // Single letters stay as lowercase for sound

    // First, play the TTS for the chunk
    console.log(`🔊 About to speak chunk: "${ttsToSpeak}" (original: "${chunk}")`)

    await speakText(ttsToSpeak, true)
    
    // Then move the chunk
    if (source === 'available') {
      setAvailableChunks(prev => prev.filter((_, i) => i !== index))
      setArrangedChunks(prev => [...prev, chunk])
    } else {
      setArrangedChunks(prev => prev.filter((_, i) => i !== index))
      setAvailableChunks(prev => [...prev, chunk])
    }
  }

  const handleContextContinue = () => {
    setShowContextIntro(false)
  }

  const handleListenToAvailableChunks = async () => {
    if (availableChunks.length === 0 || isReading) return
    const originalChunks = PhonicsChunker.chunk(currentWord.toLowerCase(), currentPattern).map(c => c.toUpperCase())
    const chunksText = originalChunks
      .map(chunk => chunk.split('').join(' '))
      .join(', ')
    console.log(`🔊 Speaking available chunks: "${chunksText}"`)
    await speakText(`Listen to the word pieces: ${chunksText}`, true)
  }

  const handleHearWord = async () => {
    if (!isReading) {
      console.log(`🔊 Speaking current word: "${currentWord}"`)
      await speakText(currentWord)
    }
  }

  const handleWordCompletionTTS = async () => {
    if (!isReading) {
      // Always say "You have built [WORD]" first
      const successMessage = `You have built ${currentWord.toUpperCase()}!`
      await speakText(successMessage)
           
    }
  }

  const handleNextWord = async () => {
    hasPlayedSuccessTTS.current = false  // Reset for next word
    if (currentWordIndex < currentWords.length - 1) {
      setCurrentWordIndex(prev => prev + 1)
      setWordsInCurrentPattern(prev => prev + 1)
  
      // Check if pattern is complete (6-8 words)
      if (wordsInCurrentPattern >= 5) {
        setShowPatternReflection(true)
        return // Don't advance to next word yet
      }
      setShowCelebration(false)
      setIsWordComplete(false)
      setShowWrongOrderMessage(false)
      setShowContextIntro(true)
      
      // Reset word tracking for next word
      setWordStartTime(Date.now())
      setHintsUsed(0)
      setResetsUsed(0)
      setCapacityAlert(null)
    } else {
      // Last word completed - track analytics but keep celebration visible
      if (userId) {
        const sessionMinutes = Math.max(1, Math.round((Date.now() - wordStartTime) / 60000))
        await trackAnyActivity('word_building', `Completed ${theme} theme`, sessionMinutes, { 
          skills: ['theme_completion', 'persistence'],
          accuracy: Math.round((wordsCompleted.length / currentWords.length) * 100),
          completed: true
        })
      }
      // Celebration stays visible with progression buttons - no navigation here
    }
  }

  const handleReset = async () => {
      let chunks = PhonicsChunker.chunk(currentWord.toLowerCase(), currentPattern).map(c => c.toUpperCase())
    
      const shuffledChunks = [...chunks].sort(() => Math.random() - 0.5)
      setAvailableChunks(shuffledChunks)
      setArrangedChunks([])
      setIsWordComplete(false)
      setShowCelebration(false)
      setShowWrongOrderMessage(false)
      setResetsUsed(prev => prev + 1)

      // ADD ANALYTICS TRACKING FOR RESET       
      if (userId && resetsUsed > 1) {         
        trackAnyActivity('word_practice', `Reset: ${currentWord}`, 0.1, { 
          skills: ['persistence'],
          completed: false 
        }).catch(error => console.log('Reset analytics failed (non-critical):', error))      
      }
    }
  
  const handleHint = async () => {
    if (arrangedChunks.length < currentWord.length) {
      let correctChunks = PhonicsChunker.chunk(currentWord.toLowerCase(), currentPattern).map(c => c.toUpperCase())
    
      const nextChunk = correctChunks[arrangedChunks.length]
      
      if (nextChunk && availableChunks.includes(nextChunk)) {
        const chunkIndex = availableChunks.indexOf(nextChunk)
        setAvailableChunks(prev => prev.filter((_, i) => i !== chunkIndex))
        setArrangedChunks(prev => [...prev, nextChunk])
        setHintsUsed(prev => prev + 1)

        // ADD ANALYTICS TRACKING FOR HINT USAGE         
        if (userId) {           
          trackAnyActivity('word_practice', `Hint used: ${currentWord}`, 0.1, { 
            skills: ['hint_usage'],
            completed: false 
          }).catch(error => console.log('Hint analytics failed (non-critical):', error))        
        }
      }
    }
  }

  const handleTryAgain = () => {
    setArrangedChunks([])
    setAvailableChunks(prev => {
      const allChunks = [...prev, ...arrangedChunks]
      return allChunks.sort(() => Math.random() - 0.5)
    })
    setShowWrongOrderMessage(false)
    setIsWordComplete(false) // Reset word completion state
  }

  const handlePersonalResponse = () => {
    // Could integrate with user's response logging in the future
    console.log('Personal connection engagement recorded')
  }

  if (!isInitialized) {
    return (
      <div className="flex-1 bg-[#FAF9F6] flex items-center justify-center p-4">
        <div className="text-center">
          <div className="text-3xl mb-2">🎯</div>
          <p className="text-sm text-[#6B7280]">Loading...</p>
        </div>
      </div>
    )
  }

  const moodStyles = getMoodStyles(currentBrainState?.id || 'focused')
  
  return (
    <div className={`min-h-screen ${moodStyles.containerBg} pb-20`}>
      <div className="max-w-4xl mx-auto">
        {/* Ambient progress bar - positioned above play area */}
        <div className="mb-4">
          <div className="h-1.5 bg-gray-200/40 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-blue-400 to-green-400 transition-all duration-500"
              style={{ width: `${((currentWordIndex + (isWordComplete ? 1 : 0)) / currentWords.length) * 100}%` }}
            />
          </div>
        </div>
        
        {/* Inline context - no modal */}

        {/* Pattern insight badge - shows after first success, not blocking */}
        {showPatternIntro && currentPatternInfo && !showLockIn && (
          <div className="fixed top-20 left-1/2 -translate-x-1/2 z-[100] animate-[slide-up_0.3s_ease-out] max-w-md mx-4">
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 border-3 border-blue-300 rounded-2xl px-5 py-4 shadow-xl">
              <div className="flex items-start gap-3">
                <span className="text-2xl">💡</span>
                <div className="flex-1">
                  <p className="text-base font-semibold text-gray-800 mb-1">
                    {currentPatternInfo.display_name}
                  </p>
                  <p className="text-sm text-gray-700">
                    {currentPatternInfo.pattern_explanation}
                  </p>
                </div>
                <button 
                  onClick={() => setShowPatternIntro(false)}
                  className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
                >
                  ×
                </button>
              </div>
            </div>
          </div>
        )}

        <>
        {/* Success handled by toast - see handleWordComplete */}
        {/* Pattern reflection moved to end-of-session toast */}
       
        {/* Gentle wrong order message */}
        
        {showWrongOrderMessage && (
            <div className="mb-3">
              <div className="bg-blue-50/60 border-l-2 border-blue-400 p-3 text-center rounded">
                <p className="text-sm text-[#4B5563]">
                  Try reordering the chunks
                </p>
                <Button
                  onClick={() => {
                    handleTryAgain()
                    setShowWrongOrderMessage(false)
                  }}
                  className={`min-h-[48px] ${moodStyles.buttonCorners}
                    bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 text-base font-medium
                    transition-all ${moodStyles.animationSpeed}
                    ${moodStyles.buttonShadow} ${moodStyles.buttonHoverShadow}
                    active:scale-97 ${moodStyles.buttonActiveShadow}`}
                >
                  🔄
                </Button>
              </div>
            </div>
        )}

        {/* Final word celebration - ONLY shows for last word */}
        {isWordComplete && currentWordIndex >= currentWords.length - 1 && (
          <div className="mb-4">
            <Card className="bg-green-50/60 border border-green-200 shadow-sm">
              <CardContent className="p-3 sm:p-4 text-center">
                <p className="text-base sm:text-lg text-[#2D3748] font-medium mb-3">
                  Amazing! You built {currentWords.length} {theme} words!
                </p>
                <p className="text-sm text-[#6B7280] mb-4">
                  Ready to use them in sentences?
                </p>
                <div className="flex flex-col sm:flex-row gap-2 justify-center">
                  <Button
                    onClick={() => {
                      localStorage.setItem('word-building-theme', theme)
                      setLocation(`/sentence-building/${theme}`)
                    }}
                    className="min-h-[48px] bg-[#10B981] hover:bg-[#059669] text-white px-6 py-2 text-sm font-medium"
                  >
                    Yes! →
                  </Button>
                  <Button
                    onClick={() => setLocation('/practice-reading')}
                    className="min-h-[48px] bg-gray-400 hover:bg-gray-500 text-white px-6 py-2 text-sm font-medium"
                  >
                    Not right now
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
        {/* Next Word Button - Shows after word completion (except last word) */}
        {isWordComplete && currentWordIndex < currentWords.length - 1 && (
          <div className="mb-4 text-center">
            <Button
              onClick={handleNextWord}
              className={`min-h-[48px] ${moodStyles.buttonCorners}
                bg-[#10B981] hover:bg-[#059669] text-white px-8 py-2 text-base font-medium
                transition-all ${moodStyles.animationSpeed}
                ${moodStyles.buttonShadow} ${moodStyles.buttonHoverShadow}
                active:scale-97 ${moodStyles.buttonActiveShadow}`}
            >
              Continue
            </Button>
          </div>
        )}
        </>

        {/* Main game area - enhanced with context awareness */}
        <Card className={`mb-4 border border-gray-200 ${moodStyles.cardBg} ${moodStyles.shadow} ${moodStyles.spacing}`}>
          <CardContent className="p-3 sm:p-4">
            
            {/* Enhanced word building area */}
            <div className="mb-6 md:mb-8">
              <div className="text-center mb-4">
                <span className={`${moodStyles.fontSize} font-bold text-[#2D3748]`}>{currentWord}</span>
              </div>
              <div className={`min-h-[80px] md:min-h-[100px] p-4 rounded-2xl border-2 border-dashed shadow-inner transition-all duration-300 ${
                showLockIn 
                  ? 'bg-green-50 border-green-400' 
                  : 'bg-white/70 border-blue-300'
              }`}>
                <div className={`flex flex-col sm:flex-row flex-wrap ${moodStyles.chunkSpacing} justify-center items-center min-h-[48px]`}>
                  {arrangedChunks.length === 0 ? (
                    <div className="text-blue-500 text-center">
                      <p className="text-sm md:text-base">Click the sounds below</p>
                    </div>
                  ) : (
                    arrangedChunks.map((chunk, index) => (
                      <button
                        key={`arranged-${index}`}
                        onClick={() => handleChunkClick(chunk, index, 'arranged')}
                        disabled={isWordComplete || isReading}
                        className={`${moodStyles.buttonSize} ${moodStyles.buttonCorners} 
                          ${showLockIn 
                            ? 'bg-green-100 border-green-500 scale-105' 
                            : 'bg-blue-200 hover:bg-blue-300 disabled:bg-blue-100 border-blue-400'
                          }
                          border-2 font-medium text-blue-900 
                          transition-all duration-300
                          ${moodStyles.buttonShadow} ${moodStyles.buttonHoverShadow} 
                          active:scale-97 ${moodStyles.buttonActiveShadow} 
                          min-w-[60px] cursor-pointer disabled:cursor-not-allowed`}
                        style={{ minHeight: '48px', touchAction: 'manipulation' }}
                      >
                        {chunk}
                      </button>
                    ))
                  )}
                </div>
              </div>
            </div>

            {/* Available chunks - raised surface */}
            <div className="mb-6 md:mb-8">
              <div className={`flex flex-wrap ${moodStyles.chunkSpacing} justify-center bg-gradient-to-b from-gray-50 to-white p-4 ${moodStyles.buttonCorners} ${moodStyles.shadow} border border-gray-200`}>
                {availableChunks.map((chunk, index) => (
                  <button
                    key={`available-${index}`}
                    onClick={() => handleChunkClick(chunk, index, 'available')}
                    disabled={isWordComplete || isReading}
                    className={`${moodStyles.buttonSize} ${moodStyles.buttonCorners}
                      bg-white hover:bg-blue-50 disabled:bg-gray-100 
                      border-2 border-blue-300 font-medium text-blue-800 
                      transition-all ${moodStyles.animationSpeed} 
                      ${moodStyles.buttonShadow} ${moodStyles.buttonHoverShadow} 
                      ${moodStyles.buttonTransform} 
                      active:scale-97 ${moodStyles.buttonActiveShadow} active:translate-y-0
                      min-w-[60px] cursor-pointer disabled:cursor-not-allowed`}
                    style={{ minHeight: '48px', touchAction: 'manipulation' }}
                  >
                    {chunk}
                  </button>
                ))}
              </div>
            </div>

            {/* Audio controls - icon-based */}
            <div className="mb-6">
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button
                  onClick={handleHearWord}
                  disabled={isReading}
                  className={`flex-1 min-h-[48px] ${moodStyles.buttonCorners}
                    bg-blue-500 hover:bg-blue-600 disabled:bg-blue-400 text-white font-medium text-base
                    transition-all ${moodStyles.animationSpeed}
                    ${moodStyles.buttonShadow} ${moodStyles.buttonHoverShadow}
                    ${moodStyles.buttonTransform}
                    active:scale-97 ${moodStyles.buttonActiveShadow} active:translate-y-0`}
                >
                  {isReading ? '🔊' : `🔊 ${currentWord}`}
                </Button>
                <Button
                  onClick={handleListenToAvailableChunks}
                  disabled={availableChunks.length === 0 || isReading}
                  className={`flex-1 min-h-[48px] ${moodStyles.buttonCorners}
                    bg-blue-500 hover:bg-blue-600 disabled:bg-blue-400 text-white text-base
                    transition-all ${moodStyles.animationSpeed}
                    ${moodStyles.buttonShadow} ${moodStyles.buttonHoverShadow}
                    ${moodStyles.buttonTransform}
                    active:scale-97 ${moodStyles.buttonActiveShadow} active:translate-y-0`}
                >
                  {isReading ? '🔊' : '🔊 Pieces'}
                </Button>
              </div>
            </div>

            {/* Game controls */}
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                onClick={handleHint}
                disabled={isWordComplete || isReading || arrangedChunks.length >= (PhonicsChunker.chunk(currentWord.toLowerCase(), currentPattern).length || 0)}
                className={`flex-1 min-h-[48px] ${moodStyles.buttonCorners}
                  bg-yellow-500 hover:bg-yellow-600 disabled:bg-yellow-300 text-white font-medium text-base
                  transition-all ${moodStyles.animationSpeed}
                  ${moodStyles.buttonShadow} ${moodStyles.buttonHoverShadow}
                  ${moodStyles.buttonTransform}
                  active:scale-97 ${moodStyles.buttonActiveShadow} active:translate-y-0`}
              >
                💡
              </Button>
              <Button
                onClick={handleReset}
                disabled={isWordComplete || isReading}
                className={`flex-1 min-h-[48px] ${moodStyles.buttonCorners}
                  bg-orange-500 hover:bg-orange-600 disabled:bg-orange-300 text-white font-medium text-base
                  transition-all ${moodStyles.animationSpeed}
                  ${moodStyles.buttonShadow} ${moodStyles.buttonHoverShadow}
                  ${moodStyles.buttonTransform}
                  active:scale-97 ${moodStyles.buttonActiveShadow} active:translate-y-0`}
              >
                🔄
              </Button>
            </div>

          </CardContent>
        </Card>

        {/* Tier 2 Breathing Break - Mandatory 10-second pause */}
        {showBreathingBreak && (
          <div className="fixed inset-0 bg-gradient-to-b from-blue-100/90 to-purple-100/90 backdrop-blur-sm z-50 flex items-center justify-center">
            <div className="bg-white/95 rounded-3xl p-8 shadow-2xl max-w-md mx-4 text-center">
              <div className="text-6xl mb-4 animate-pulse">🌬️</div>
              <h2 className="text-2xl font-bold text-gray-800 mb-4">Brain Break Time!</h2>
              <p className="text-lg text-gray-600 mb-6">Take 3 deep breaths with me...</p>
              
              {/* Breathing animation circle */}
              <div className="relative w-32 h-32 mx-auto mb-4">
                <div className="absolute inset-0 bg-blue-200 rounded-full animate-[ping_3s_ease-in-out_infinite]"></div>
                <div className="absolute inset-4 bg-blue-400 rounded-full animate-[pulse_3s_ease-in-out_infinite]"></div>
              </div>
              
              <p className="text-sm text-gray-500">Breathe in... breathe out... breathe in...</p>
            </div>
          </div>
        )}

        {/* Inline Toast - Success Feedback */}
        {showToast && (
          <div className="fixed bottom-20 left-1/2 -translate-x-1/2 z-50 animate-[slide-up_0.3s_ease-out]">
            <div className="bg-[#10B981] text-white px-4 py-2 rounded-lg shadow-lg flex items-center gap-2">
              <span className="text-lg">{toastMessage}</span>
            </div>
          </div>
        )}

        {/* Progress indicator - minimal visual dots only, no counters */}
        {!moodStyles.hideProgress && (
          <div className="mb-4 text-center">
            <div className="flex justify-center gap-2">
              {currentWords.map((_, index) => (
                <div 
                  key={index}
                  className={`w-2 h-2 rounded-full transition-all duration-300 ${
                    index < currentWordIndex
                      ? 'bg-green-500 scale-110' 
                      : index === currentWordIndex
                      ? 'bg-blue-500 scale-125'
                      : 'bg-gray-300'
                  }`}
                />
              ))}
            </div>
          </div>
        )}

        {/* Pattern progress summary - appears after 2+ words */}
        {wordsCompleted.length >= 2 && Object.keys(patternProgress).length > 0 && (
          <div className="mt-3 bg-blue-50/40 rounded p-2 border border-blue-100">
            <h4 className="text-xs font-medium text-[#4B5563] mb-1.5">Pattern Progress:</h4>
            <div className="flex flex-wrap gap-2">
              {Object.entries(patternProgress).map(([family, progress]) => (
                <span key={family} className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded">
                  {family.replace('_family', '').replace('_', ' ')}: {progress.wordsCompleted.length}
                </span>
              ))}
            </div>
          </div>
        )}

      </div>
    </div>
  )
}

export default WordBuildingGamePage